import { useCallback, useEffect, useState } from 'react';
import { Container, Title, Text, Stack, Center, Loader } from '@mantine/core';
import { DollarSign } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { coachPortalApi } from '../../services/api/coachPortal';
import type { CoachCompensationCohort } from '../../types';
import { EmptyState } from '../../components/common';
import { CohortCompensationCard } from './components/CohortCompensationCard';

/**
 * Coach Portal: Compensation Page (E13-4 / MED-508 AC7-AC11).
 * Lists all cohorts with compensation data for the logged-in coach.
 */
export function CoachCompensationPage() {
  const [cohorts, setCohorts] = useState<CoachCompensationCohort[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCompensation = useCallback(async () => {
    setLoading(true);
    try {
      const data = await coachPortalApi.getCompensation();
      setCohorts(data.cohorts ?? []);
    } catch (error) {
      console.error('Failed to load compensation:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load compensation data',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchCompensation();
  }, [fetchCompensation]);

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Compensation</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Your compensation across all programs
          </Text>
        </div>

        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : cohorts.length === 0 ? (
          <EmptyState
            icon={<DollarSign size={32} />}
            title="Compensation details are being prepared"
            description="You'll see your compensation here once it's been calculated for your assigned cohorts."
          />
        ) : (
          <Stack gap="md">
            {cohorts.map(cohort => (
              <CohortCompensationCard key={cohort.id} cohort={cohort} />
            ))}
          </Stack>
        )}

        {cohorts.length > 0 && (
          <Text size="xs" c="dimmed" ta="center" maw={700} mx="auto">
            Compensation amounts are estimates. Guaranteed monthly payments are pre-scheduled.
            Attendance-dependent amounts are paid within 10 days of event delivery.
          </Text>
        )}
      </Stack>
    </Container>
  );
}

export default CoachCompensationPage;
